import type { tasks } from '@cwos/api-client';
import { Pill } from '@cwos/ui-admin';
import { PersonChip } from '../PersonChip.js';
import type { PersonDirectory } from '../personDirectory.js';
import { completionPresentation } from './taskLogic.js';

/**
 * WHAT A TASK'S COMPLETION MEANS, SAID ON SCREEN.
 *
 * This component is the reason the Tasks domain has three completion modes, and
 * it is the place where the whole design can be thrown away silently. Five
 * database constraints, a route file with no verification endpoint, and a
 * contract that spells out `verified` as "Phibian observed the condition
 * resolve" all exist so that the product cannot claim Phibian saw something it
 * did not. One checkmark rendered over `completion_claimed` undoes every one of
 * them, and nothing about that failure looks like a failure.
 *
 * So the three modes are made VISIBLY DISTINCT here, in three ways at once:
 *
 *   1. THE AFFIRMATION. "Phibian verified this." is rendered ONLY when
 *      `completionClaim(task).phibianVerified` is true, which is true only
 *      when the verification stamp (`verifiedAt` AND `verificationActor`) is
 *      present. It appears nowhere else in this app.
 *   2. THE ATTRIBUTION. A claim is rendered as a PERSON — their face, their
 *      name, their words in quotation marks — because a claim belongs to
 *      somebody and an unattributed "Completed ✓" belongs to the system.
 *   3. THE ASSURANCE SENTENCE. Every non-verified completion says, in plain
 *      words, that it is not verified and WHY: `human_attested` because Phibian
 *      cannot observe this outcome at all, `hybrid` because Phibian is still
 *      watching and has not seen the condition resolve.
 *
 * `data-completion` carries the same four-value vocabulary to the DOM so a test
 * can assert the settled state rather than infer it from prose.
 */
export interface CompletionStatementProps {
  task: tasks.TaskView;
  people: PersonDirectory;
  /** The detail view shows the attestation's own words; a row does not. */
  showStatement?: boolean;
}

export function CompletionStatement({
  task,
  people,
  showStatement = false,
}: CompletionStatementProps) {
  const view = completionPresentation(task, (id) => people.name(id));

  return (
    <div className="task-completion" data-completion={view.token}>
      <Pill variant={view.variant}>{view.label}</Pill>

      {/*
        The affirmation. `view.affirmation` is non-null for exactly one of the
        four completion kinds, and this is the only element that renders it.
      */}
      {view.affirmation ? (
        <p className="task-completion-verified" data-affirmation="phibian-verified">
          {view.affirmation}
        </p>
      ) : null}

      {/*
        The claim, as a person's claim. Rendered for every attested task —
        including a verified one, because a person did attest first and erasing
        them once Phibian confirmed it would rewrite the history the domain
        keeps deliberately immutable.
      */}
      {view.claimSentence ? (
        <div className="task-completion-claim">
          <PersonChip person={people.person(view.claimantUserId)} />
          <p className="task-completion-claim-sentence">{view.claimSentence}</p>
          {showStatement && task.attestationStatement ? (
            <blockquote className="task-completion-statement">
              “{task.attestationStatement}”
            </blockquote>
          ) : null}
        </div>
      ) : null}

      <p className="task-completion-assurance">{view.assuranceSentence}</p>
    </div>
  );
}
